<template>
 <ValidationObserver ref="formObserver" v-slot="{ handleSubmit }">
  <form @submit.prevent="handleSubmit(onSubmit)">
     <div class="rounded-lg bg-smoke-100 py-4 px-2 lg:px-4 md:py-7.5 md:px-12 xl:px-56">
      <div class="flex border-b border-dark-b text-xl md:text-2xl font-semibold">
        Supplier Selection Criteria
      </div>
      <p class="lg:py-12 pb-9 lg:pb-6 pt-9 text-base lg:text-lg">
        An average Sourcing Expert can find 3-5 suppliers per project. 
        Please be sure to provide good, clear information for them in this section 
        including the market(s) where you plan to sell your product. 
      </p>
      <div class="flex flex-wrap w-full">
        <div class="pb-1 font-semibold text-lg lg:text-xl leading-5 lg:leading-normal">
          Project Description or <br class="lg:hidden"/>Special Requirement
        </div>
        <textarea
          class="border-2 rounded-lg p-3 w-full text-lg lg:h-32 h-40"
          name="productNameAndDescription"
          placeholder="Tell us about the product and any special requirements you may have here."
          v-model="formData.supplier_special_requirement"
        />
      </div>
      <div class="flex flex-wrap w-full pt-5 lg:pt-4">
        <div class="pb-1 font-semibold text-lg lg:text-xl leading-5 lg:leading-normal">
          Any Audits or <br class="lg:hidden"/>Compliance Certificate required?
        </div>
        <div class="w-full flex flex-wrap">
          <textarea
            class="border-2 rounded-lg p-3 w-full text-lg lg:h-32 h-40"
            name="company"
            placeholder="e.g. ISO, BSI, SMETA etc."
            v-model="formData.supplier_compliance_requirement"
          />
        </div>
      </div>
      <div class="flex flex-wrap w-full pt-5 lg:pt-4">
        <div class="pb-1 font-semibold text-lg lg:text-xl tracking-tight lg:tracking-normal">
          What countries/regions do you want your Sourcing Expert to find suppliers in?
          <span class="text-base lg:text-sm font-normal">(Select up to 3 from the picklist)</span>
        </div>
        <div class="w-full flex flex-col items-center lg:flex-row lg:flex-nowrap ">
          <CountrySelect
            name="country"
            class="form-add-input country-select text-lg w-full lg:w-auto"
            placeholder="Country/Region"
            ref="countrySelect"
            :country="location"
            :class="{'text-opacity-50': !location}"
            v-model="location"
          />
          <button
            class="w-54 lg:w-62-5 h-12 lg:ml-5 ml-2 py-1 mt-5 lg:mt-0 rounded-lg bg-dark-blue 
            text-white text-lg lg:text-xl font-semibold"
            type="button"
            :disabled="formData.regions.length > 2"
            :class="{'opacity-50': formData.regions.length > 2}"
            @click="addLocation"
          >Click to Add Country</button>
        </div>
        <div class="flex flex-wrap h-8 pt-3 mb-5">
          <div
            v-for="(loc, index) in display_regions"
            :key="`loc-${index}`"
            class="text-xs mr-4 px-2 py-1 bg-gray-b rounded-lg text-opacity-50"
          >
            <span class="pr-2">{{ loc }}</span>
            <span class="cursor-pointer text-lg" @click="removeLocation(index)">x</span>
          </div>
        </div>
      </div>
      <div class="flex border-b border-dark-b text-xl md:text-2xl font-semibold lg:pt-14 pt-10">
        Supplier Questions
      </div>
      <p class="lg:mt-12 mt-10 text-base lg:text-lg leading-5 lg:leading-normal">
        Our Sourcing Expert will request basic product information from your
        supplier including: MOQ, shipping terms, pricing, packing details,
        container capacity, and product photos. In addition, they will ask the
        following standardized questions.
      </p>
      <ul class="list-inside mt-3 list-disc text-base lg:text-lg leading-5 lg:leading-normal tracking-tight lg:tracking-normal">
        <li class="m-2">Who are your top 3 largest customers?</li>
        <li class="m-2">What is your top 3 export markets?</li>
        <li class="m-2">Which are your top 3 best-selling products?</li>
        <li class="m-2">What is your manufacturing speciality?</li>
        <li class="m-2">What are your lead times and payment terms?</li>
        <li class="m-2">What is the closest FOB Port?</li>
        <li class="m-2">What is your monthly production capacity?</li>
        <li class="m-2">Are you a manufacturer or an agent?</li>
        <li class="m-2">
          Are your products available for my market to distribute on an
          exclusive basis?
        </li>
        <li class="m-2 text-base lg:text-lg">
          What product certifications or factory audit certifications do you
          have? (A copy will be requested)
        </li>
      </ul>
      <div class="w-full pt-9">
        <div class="pb-1 text-lg lg:text-xl font-semibold">
          Add up to 3 custom questions you would like to ask suppliers here
          <span class="text-base lg:text-sm font-normal">(Type your questions below)</span>
        </div>
        <div class="flex flex-col lg:flex-row lg:flex-nowrap items-center justify-between">
          <textarea
            class="border-2 rounded-lg p-3 w-full text-lg lg:h-32 h-40"
            name="questions"
            placeholder="Ask your questions here..."
            v-model="question"
          />
          <button
            class="w-54 lg:w-80 h-12 lg:ml-5 ml-2 mt-5 mb-2 lg:my-0 px-1 rounded-lg bg-dark-blue 
            text-white text-lg lg:text-xl font-semibold"
            type="button"
            :disabled="formData.questions.length > 2"
            :class="{'opacity-50': formData.questions.length > 2}"
            @click="addQuestion"
          >Click to Add Questions</button>
        </div>
        <div class="flex flex-wrap" :class="{'pt-2' : formData.questions.length}">
          <div
            v-for="(que, index) in formData.questions"
            :key="`que-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">{{ que }}</span>
              <span class="cursor-pointer text-lg" @click="removequestion(index)">x</span>
            </div>
          </div>
        </div>
     </div>
     <div class="flex justify-between md:justify-end pt-5 lg:pt-12 lg:pb-10 pb-8">
          <button 
            class="text-blue border-2 border-blue rounded-full py-3 px-3 md:px-8 text-lg lg:text-xl font-bold mr-3"
            :class="{'opacity-50' : isSavingData}"
            @click="PreviousTab" 
          >
            Back
          </button>
          &nbsp;
          &nbsp;
          <br/>
          <button 
            class="bg-blue text-white rounded-full py-3 px-3 md:px-8 text-lg lg:text-xl font-bold"
            :disabled="isSavingData"
            :class="{'opacity-50' : isSavingData}"
          >
            Save & Continue
          </button>
        </div>
    </div>  
  </form>
  <div class="text-center not-ready-section pb-3 pt-6">
    Not ready to commit? Questions? Let’s talk <br class="md:hidden"/> about your needs!
    <br/>
  </div>
  <div class="flex justify-center items-center">
    <button @click="getBookACallUrl" class="bg-theme  text-white rounded-full py-2 px-5 text-lg lg:text-xl font-semibold">
      Book a Call
    </button>
  </div>
</ValidationObserver>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import countriesArray from '@/assets/json/countries.json'

export default {
  name: "SupplierCriteriaForm",
  props: {
    userData: {
      type: Object,
      default: null
    },
  },
  created() {
    this.formData = { ...this.formData, ...this.userData }
    if (this.formData.regions) {
      this.formData.regions.forEach(regionCode => {
        const country = this.countriesArray.find(country => country.code === regionCode)
        if (regionCode === 'KR') this.display_regions.push('South Korea') 
        else this.display_regions.push(country.name)
      })
    }
    this.setDraftData()
  },
  data() {
    return {
      formData: {
        regions: [],
        questions: []
      },
      display_regions: [],
      location: 'CN',
      question: '',
      goOnNextPage: false,
      countriesArray,
      topCountries: ['BD','TW','JP','KR','TH','MY','ID','KH','MM','VN','IN','CN'],
      isSavingData: false
    };
  },
  computed: {
    ...mapGetters ([
      'isUserLoggedIn',
      'getSourcingToken'
    ]),
    isValidLocation() {
      return !this.formData.regions.some(loc => loc === this.location)
    },
    isValidQuestion() {
      return !this.formData.questions.some(question => question === this.question)
    },
  },
  watch: {
    isUserLoggedIn() {
      if (this.isUserLoggedIn && this.goOnNextPage) this.proceedToNextPage()
    }
  },
  mounted() {
    this.setTopCountries()   
    if(localStorage.getItem('supplierCriteriaDetails') && this.$route.query.editing) {
      let FormDetails = localStorage.getItem('supplierCriteriaDetails')
      localStorage.removeItem('supplierCriteriaDetails')
      FormDetails = JSON.parse(FormDetails)
      this.updateSupplierInformation({ ...FormDetails, _method: 'PUT' })
      this.$emit('proceedNext')
    } 
  },
  methods: {
    ...mapActions ([
      'LoginSelectionMode',
      'openLoginModal',
      'updateSupplierInformation',
      'linkClientToSourcingRequest'
    ]),
    getBookACallUrl() {
      window.open(process.env.VUE_APP_BOOK_A_CALL_URL,'_blank');      
    },
    PreviousTab() {
      this.$emit('changeTab')
    },
    setDraftData() {
      if (this.userData && this.userData.custom_questions) {
        this.formData.questions = this.userData.custom_questions
        delete this.formData.custom_questions
      }
      if (this.userData && this.userData.supplier_region) {      
        this.display_regions = this.userData.supplier_region
        this.display_regions.forEach(region => {
          const country = this.countriesArray.find(country => country.name === region)
          if (country.code === 'KR') this.display_regions[this.display_regions.indexOf(region)] = 'South Korea'
          this.formData.regions.push(country.code)
        })
        delete this.formData.supplier_region
      }
    },
    setTopCountries() {
      let selectTag = this.$refs.countrySelect.$el
      const placeHolderTag = selectTag.firstChild
      const hk = selectTag.querySelector('[value="HK"]')
      hk.innerHTML = 'Hong Kong SAR'
      const sk = selectTag.querySelector('[value="KR"]')
      sk.innerHTML = 'South Korea'
      const tw = selectTag.querySelector('[value="TW"]')
      tw.innerHTML = 'Taiwan R.O.C'
      this.topCountries.forEach(country => {
        const countryTag = selectTag.querySelector(`[value=${country}`)
        placeHolderTag.after(countryTag)
      })
      selectTag.value = 'CN'
    },
    addLocation() {
      const options = this.$refs.countrySelect.$el

      if (this.location && this.formData.regions.length < 3 && this.isValidLocation) {
        this.formData.regions.push(this.location)
        this.display_regions.push(options[options.selectedIndex].text)
        this.location = ''
      }
    },
    removeLocation(index) {
      this.formData.regions.splice(index, 1)
      this.display_regions.splice(index, 1)
    },
    addQuestion() {
      let question_length = this.question.trim().length
      if (this.question.length && this.formData.questions.length < 3 && this.isValidQuestion && question_length > 0) {
        this.formData.questions.push(this.question)
        this.question = ''
      }
    },
    removequestion(index) {
      this.formData.questions.splice(index, 1)
    },
    onSubmit() {
      this.goOnNextPage = true
      this.addLocation()
      this.addQuestion()
      if (this.isUserLoggedIn) {
        this.proceedToNextPage()
      } else {
        localStorage.setItem('previousUrl', window.location.href)
        localStorage.setItem('existingSourcingToken', this.getSourcingToken)
        localStorage.setItem('activeTab', 4)
        localStorage.setItem('supplierCriteriaDetails', JSON.stringify(this.formData))
        this.proceedToNextPage()
        this.LoginSelectionMode('buyer')
        this.$router.push({name: 'LoginFormv2'})
      }
    },
    async proceedToNextPage() {
      this.isSavingData = true
      this.$emit('updateSourcingForm', { supplierData: this.formData })
      let response = await this.linkClientToSourcingRequest({ _method: 'PUT' })
      if (response.result) {
        await this.updateSupplierInformation({ ...this.formData, _method: 'PUT' })
      }
      if (response.result) this.$emit('proceedNext')
      this.isSavingData = false
    }
  }
};
</script>
<style lang="scss" scoped>
.not-ready-section {
  @screen md {
  font-size: 26px;
  line-height: 39px;
  font-weight: 700;
  }
  font-size: 24px;
  line-height: 28px;
  font-weight: 600;
}
  .country-select {
    @screen lg { background-position: 98% center; }
  }
</style>
